import axiosInstance from '../config/axios.config';

/**
 * Loan Management API Service
 * 
 * Provides methods to interact with loan types and loan products APIs
 */
class LoanService {
    
    // ================ LOAN TYPES ================
    
    /**
     * Get all loan types with optional filters
     */
    async getLoanTypes(filters = {}) {
        try {
            const response = await axiosInstance.get('/loan-types', { params: filters });
            return response.data;
        } catch (error) {
            console.error('Error fetching loan types:', error);
            throw error;
        }
    }

    /**
     * Get active loan types only
     */
    async getActiveLoanTypes() {
        try {
            const response = await axiosInstance.get('/loan-types/active');
            return response.data;
        } catch (error) {
            console.error('Error fetching active loan types:', error);
            throw error;
        }
    }

    /**
     * Get loan types by category
     */
    async getLoanTypesByCategory(category) {
        try {
            const response = await axiosInstance.get(`/loan-types/category/${category}`);
            return response.data;
        } catch (error) {
            console.error('Error fetching loan types by category:', error);
            throw error;
        }
    }

    /**
     * Get specific loan type by ID
     */
    async getLoanType(id) {
        try {
            const response = await axiosInstance.get(`/loan-types/${id}`);
            return response.data;
        } catch (error) {
            console.error('Error fetching loan type:', error);
            throw error;
        }
    }

    /**
     * Create new loan type
     */
    async createLoanType(loanTypeData) {
        try {
            const response = await axiosInstance.post('/loan-types', loanTypeData);
            return response.data;
        } catch (error) {
            console.error('Error creating loan type:', error);
            throw error;
        }
    }

    /**
     * Update loan type
     */
    async updateLoanType(id, loanTypeData) {
        try {
            const response = await axiosInstance.put(`/loan-types/${id}`, loanTypeData);
            return response.data;
        } catch (error) {
            console.error('Error updating loan type:', error);
            throw error;
        }
    }

    /**
     * Delete loan type
     */
    async deleteLoanType(id, force = false) {
        try {
            const url = force ? `/loan-types/${id}?force=true` : `/loan-types/${id}`;
            const response = await axiosInstance.delete(url);
            return response.data;
        } catch (error) {
            console.error('Error deleting loan type:', error);
            throw error;
        }
    }

    /**
     * Get loan type statistics
     */
    async getLoanTypeStatistics() {
        try {
            const response = await axiosInstance.get('/loan-types/statistics');
            return response.data;
        } catch (error) {
            console.error('Error fetching loan type statistics:', error);
            throw error;
        }
    }

    /**
     * Search loan types
     */
    async searchLoanTypes(query) {
        try {
            const response = await axiosInstance.get('/loan-types/search', { params: { q: query } });
            return response.data;
        } catch (error) {
            console.error('Error searching loan types:', error);
            throw error;
        }
    }

    /**
     * Get available loan categories
     */
    async getLoanCategories() {
        try {
            const response = await axiosInstance.get('/loan-types/categories');
            return response.data;
        } catch (error) {
            console.error('Error fetching loan categories:', error);
            throw error;
        }
    }

    // ================ LOAN PRODUCTS ================

    /**
     * Get all loan products with optional filters
     */
    async getLoanProducts(filters = {}) {
        try {
            const response = await axiosInstance.get('/loan-products', { params: filters });
            return response.data;
        } catch (error) {
            console.error('Error fetching loan products:', error);
            throw error;
        }
    }

    /**
     * Get active loan products only
     */
    async getActiveLoanProducts() {
        try {
            const response = await axiosInstance.get('/loan-products/active');
            return response.data;
        } catch (error) {
            console.error('Error fetching active loan products:', error);
            throw error;
        }
    }

    /**
     * Get featured loan products
     */
    async getFeaturedLoanProducts() {
        try {
            const response = await axiosInstance.get('/loan-products/featured');
            return response.data;
        } catch (error) {
            console.error('Error fetching featured loan products:', error);
            throw error;
        }
    }

    /**
     * Get loan products by loan type
     */
    async getLoanProductsByType(loanTypeId) {
        try {
            const response = await axiosInstance.get(`/loan-products/loan-type/${loanTypeId}`);
            return response.data;
        } catch (error) {
            console.error('Error fetching loan products by type:', error);
            throw error;
        }
    }

    /**
     * Get specific loan product by ID
     */
    async getLoanProduct(id) {
        try {
            const response = await axiosInstance.get(`/loan-products/${id}`);
            return response.data;
        } catch (error) {
            console.error('Error fetching loan product:', error);
            throw error;
        }
    }

    /**
     * Create new loan product
     */
    async createLoanProduct(loanProductData) {
        try {
            const response = await axiosInstance.post('/loan-products', loanProductData);
            return response.data;
        } catch (error) {
            console.error('Error creating loan product:', error);
            throw error;
        }
    }

    /**
     * Update loan product
     */
    async updateLoanProduct(id, loanProductData) {
        try {
            const response = await axiosInstance.put(`/loan-products/${id}`, loanProductData);
            return response.data;
        } catch (error) {
            console.error('Error updating loan product:', error);
            throw error;
        }
    }

    /**
     * Delete loan product
     */
    async deleteLoanProduct(id) {
        try {
            const response = await axiosInstance.delete(`/loan-products/${id}`);
            return response.data;
        } catch (error) {
            console.error('Error deleting loan product:', error);
            throw error;
        }
    }

    /**
     * Calculate EMI for loan product
     */
    async calculateEMI(productId, loanAmount, tenureMonths, customerRisk = {}) {
        try {
            const response = await axiosInstance.post(`/loan-products/${productId}/calculate-emi`, {
                loan_amount: loanAmount,
                tenure_months: tenureMonths,
                customer_risk: customerRisk
            });
            return response.data;
        } catch (error) {
            console.error('Error calculating EMI:', error);
            throw error;
        }
    }

    /**
     * Generate pricing quote
     */
    async generatePricingQuote(productId, parameters) {
        try {
            const response = await axiosInstance.post(`/loan-products/${productId}/pricing-quote`, parameters);
            return response.data;
        } catch (error) {
            console.error('Error generating pricing quote:', error);
            throw error;
        }
    }

    /**
     * Get promotional offers
     */
    async getPromotionalOffers(productId, customerSegment = 'new') {
        try {
            const response = await axiosInstance.get(`/loan-products/${productId}/promotional-offers`, {
                params: { customer_segment: customerSegment }
            });
            return response.data;
        } catch (error) {
            console.error('Error fetching promotional offers:', error);
            throw error;
        }
    }

    /**
     * Check product eligibility
     */
    async checkEligibility(productId, customerProfile) {
        try {
            const response = await axiosInstance.post(`/loan-products/${productId}/check-eligibility`, {
                customer_profile: customerProfile
            });
            return response.data;
        } catch (error) {
            console.error('Error checking eligibility:', error);
            throw error;
        }
    }

    /**
     * Get product recommendations
     */
    async getProductRecommendations(customerProfile, limit = 5) {
        try {
            const response = await axiosInstance.post('/loan-products/recommendations', {
                customer_profile: customerProfile,
                limit: limit
            });
            return response.data;
        } catch (error) {
            console.error('Error fetching product recommendations:', error);
            throw error;
        }
    }

    /**
     * Compare loan products
     */
    async compareProducts(productIds) {
        try {
            const response = await axiosInstance.post('/loan-products/compare', {
                product_ids: productIds
            });
            return response.data;
        } catch (error) {
            console.error('Error comparing products:', error);
            throw error;
        }
    }

    /**
     * Search loan products
     */
    async searchLoanProducts(query) {
        try {
            const response = await axiosInstance.get('/loan-products/search', { params: { q: query } });
            return response.data;
        } catch (error) {
            console.error('Error searching loan products:', error);
            throw error;
        }
    }

    /**
     * Get loan product statistics
     */
    async getLoanProductStatistics() {
        try {
            const response = await axiosInstance.get('/loan-products/statistics');
            return response.data;
        } catch (error) {
            console.error('Error fetching loan product statistics:', error);
            throw error;
        }
    }

    /**
     * Get popular products
     */
    async getPopularProducts(limit = 10) {
        try {
            const response = await axiosInstance.get('/loan-products/popular', { params: { limit } });
            return response.data;
        } catch (error) {
            console.error('Error fetching popular products:', error);
            throw error;
        }
    }

    /**
     * Bulk update product status
     */
    async bulkUpdateStatus(productIds, status) {
        try {
            const response = await axiosInstance.post('/loan-products/bulk-status-update', {
                product_ids: productIds,
                status: status
            });
            return response.data;
        } catch (error) {
            console.error('Error bulk updating product status:', error);
            throw error;
        }
    }
}

export default new LoanService();